import { captureException } from '@sentry/browser'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { EndJobsMutation } from '@/components/Job/graphql/mutations'
import { $t, $tc } from '@/composables/translation'
import { useConfirmModal } from '@/composables/useConfirmModal'

export interface EndJobsResult {
  endedCount: number
  skippedCount: number
  hasError: boolean
}

export const endJobs = async (jobIds: string[]): Promise<EndJobsResult> => {
  try {
    const result = await publicApi.mutate({
      mutation: EndJobsMutation,
      variables: {
        input: {
          jobIds,
          accountId: currentAccount.globalId,
        },
      },
      context: { skipGlobalErrorHandling: true },
    })

    const payload = result.data?.endJobs

    return {
      endedCount: payload?.ended.length ?? 0,
      skippedCount: payload?.skipped.length ?? 0,
      hasError: payload == null,
    }
  } catch {
    return { endedCount: 0, skippedCount: 0, hasError: true }
  }
}

const GRAPHQL_TABLE_GLOBALS_WRAPPER = '.graphql-table-globals-wrapper'

export async function endJobsWithConfirmation(ids: string[]): Promise<void> {
  const { openConfirmModal } = useConfirmModal()
  let endJobsResult: EndJobsResult | undefined
  const confirmed = await openConfirmModal({
    actionName: $tc('Complete job | Complete jobs', ids.length),
    title: $t('Are you sure?'),
    description: $tc(
      'Do you want to <strong>terminate this job</strong> and terminate any ongoing job process? | Do you want to <strong>terminate the :count jobs</strong>, and any ongoing process?',
      ids.length,
    ),
    template: 'delete',
    confirmText: $tc('Mark the job as completed | Mark the jobs as completed', ids.length),
    loadingTitle: $t('Processing'),
    teleportTarget: GRAPHQL_TABLE_GLOBALS_WRAPPER,
    onConfirm: async () => {
      endJobsResult = await endJobs(ids)
      await publicApi.refetchQueries({ include: 'active' }).catch(captureException)
    },
  })

  if (!confirmed || !endJobsResult) {
    return
  }

  const { endedCount, skippedCount, hasError } = endJobsResult

  if (hasError) {
    await openConfirmModal({
      title: $t('Error'),
      description: $t(
        'One or more jobs failed to be ended. Try again later or reach out to support for help.',
      ),
      template: 'danger',
      hideCancel: true,
      confirmText: $t('OK'),
      teleportTarget: GRAPHQL_TABLE_GLOBALS_WRAPPER,
    })
    return
  }

  const messages = [
    endedCount > 0 ? $tc(':count job ended | :count jobs ended', endedCount) : null,
    skippedCount > 0
      ? $tc(
          ':count job could not be ended and was skipped | :count jobs could not be ended and were skipped',
          skippedCount,
        )
      : null,
  ].filter((message): message is string => message != null)

  await openConfirmModal({
    title: $t('Success'),
    description: messages.join(' '),
    template: 'confirmation',
    hideCancel: true,
    confirmText: $t('OK'),
    teleportTarget: GRAPHQL_TABLE_GLOBALS_WRAPPER,
  })
}
